import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Save, Edit, Trash2, Phone, RotateCcw } from "lucide-react";

const PROFILE_LABEL = {
  front_desk_ambiance: "Front Desk & Ambience",
  call_center: "Focused on Call",
  cash_documentation: "Cash & Documentation",
};

const DailyLog = () => {
  const { user, receptionists } = useOutletContext();
  const [empId, setEmpId] = useState("");
  const [profileFilter, setProfileFilter] = useState("all");
  const [profileFields, setProfileFields] = useState(null);
  const [values, setValues] = useState({});
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [remarks, setRemarks] = useState("");
  const [recentLogs, setRecentLogs] = useState([]);
  const [editingLog, setEditingLog] = useState(null);

  useEffect(() => {
    api.get("/meta/profile-fields").then((r) => setProfileFields(r.data));
  }, []);

  const profilesOf = (r) => (Array.isArray(r?.profile_types) && r.profile_types.length > 0 ? r.profile_types : (r?.profile_type ? [r.profile_type] : []));

  const filteredReceptionists = useMemo(() => {
    if (profileFilter === "all") return receptionists;
    return receptionists.filter((r) => profilesOf(r).includes(profileFilter));
  }, [profileFilter, receptionists]);

  useEffect(() => {
    if (user.role === "receptionist" && user.employee_id) {
      setEmpId(user.employee_id);
    } else if (filteredReceptionists.length > 0 && !filteredReceptionists.find((r) => r.employee_id === empId)) {
      setEmpId(filteredReceptionists[0].employee_id);
    }
  }, [user, filteredReceptionists]);

  const currentRec = useMemo(
    () => receptionists.find((r) => r.employee_id === empId),
    [empId, receptionists]
  );

  const currentProfiles = useMemo(() => profilesOf(currentRec), [currentRec]);

  // Build union of fields across all assigned profiles, grouped by profile for display
  const sharedFields = profileFields?.shared || [];
  const fieldsByProfile = useMemo(() => {
    if (!profileFields) return {};
    const out = {};
    for (const pt of currentProfiles) {
      out[pt] = profileFields.by_profile[pt] || [];
    }
    return out;
  }, [profileFields, currentProfiles]);

  const loadLogs = () => {
    if (!empId) return;
    api.get(`/daily-logs?employee_id=${empId}`).then((r) => setRecentLogs(r.data.slice(0, 12)));
  };
  useEffect(loadLogs, [empId]);

  const handleField = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const submit = async () => {
    if (!empId) return toast.error("Select a receptionist first");
    try {
      if (editingLog) {
        await api.put(`/daily-logs/${editingLog.log_id}`, { values, feedback_notes: notes, remarks });
        toast.success("Log updated");
        setEditingLog(null);
      } else {
        await api.post("/daily-logs", { employee_id: empId, date, values, feedback_notes: notes, remarks });
        toast.success("Daily log saved");
      }
      setValues({}); setNotes(""); setRemarks(""); loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const beginEdit = (log) => {
    setEditingLog(log);
    setValues(log.values || {});
    setNotes(log.feedback_notes || "");
    setRemarks(log.remarks || "");
    setDate(log.date);
    setEmpId(log.employee_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingLog(null); setValues({}); setNotes(""); setRemarks("");
  };

  const deleteLog = async (log_id) => {
    if (!window.confirm("Delete this daily log? This cannot be undone.")) return;
    try {
      await api.delete(`/daily-logs/${log_id}`);
      toast.success("Deleted");
      if (editingLog?.log_id === log_id) cancelEdit();
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const resetFilters = () => {
    setProfileFilter("all");
    setDate(new Date().toISOString().slice(0, 10));
    if (filteredReceptionists.length > 0) setEmpId(filteredReceptionists[0].employee_id);
  };

  const renderField = (f) => {
    const val = values[f.key];
    if (f.unit === "yesno") {
      return (
        <div key={f.key} className="flex items-center justify-between p-3 rounded-lg bg-[#0F172A] border border-[#334155]">
          <div>
            <div className="text-sm text-slate-200">{f.label}</div>
            <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mt-1">Weight {f.weight}</div>
          </div>
          <Switch data-testid={`field-${f.key}`} checked={!!val} onCheckedChange={(v) => handleField(f.key, v)} className="data-[state=checked]:bg-[#059669]" />
        </div>
      );
    }
    return (
      <div key={f.key}>
        <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
          {f.label} {f.unit === "inr" ? "(₹)" : f.unit === "star" ? "(★1-5)" : f.unit === "percent" ? "(%)" : ""}
        </Label>
        <Input
          data-testid={`field-${f.key}`}
          type="number"
          step={f.unit === "star" ? "0.1" : "any"}
          min="0"
          max={f.unit === "star" ? 5 : f.unit === "percent" ? 100 : undefined}
          value={val ?? ""}
          onChange={(e) => handleField(f.key, e.target.value === "" ? "" : Number(e.target.value))}
          className="bg-[#0F172A] border-[#334155] text-white"
        />
      </div>
    );
  };

  // Section achievement helper (per profile)
  const sectionAchievement = (fields) => {
    const scored = fields.filter((f) => values[f.key] !== undefined && values[f.key] !== "");
    if (!scored.length) return null;
    const summed = scored.reduce((a, f) => {
      const v = typeof values[f.key] === "boolean" ? (values[f.key] ? 1 : 0) : Number(values[f.key] || 0);
      return a + (v > 0 ? 1 : 0);
    }, 0);
    return Math.round((summed / scored.length) * 100);
  };

  // Calling specific
  const callingRequired = !!currentRec?.calling_required || currentProfiles.includes("call_center");
  const callingTarget = Number(currentRec?.daily_calling_target || 0);
  const callsTalked = Number(values.calls_talked || 0);
  const callingAchievement = callingTarget > 0 ? Math.round((callsTalked / callingTarget) * 100) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Daily Activity</div>
          <h1 className="font-heading text-4xl text-white tracking-tight mt-1">
            {editingLog ? "Editing Log" : "Reception Log"}
          </h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Fields auto-load from the receptionist's assigned Profile Type(s) in Master Data. Multi-profile receptionists see every KPI section in one record.
          </p>
        </div>
        {editingLog && (
          <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500 font-mono text-xs">
            Editing {editingLog.date} · {editingLog.log_id}
          </Badge>
        )}
      </div>

      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
              Profile Type Filter
            </Label>
            <Select value={profileFilter} onValueChange={setProfileFilter}>
              <SelectTrigger data-testid="profile-type-filter" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                <SelectItem value="all">All Profiles</SelectItem>
                <SelectItem value="front_desk_ambiance">Front Desk & Ambience</SelectItem>
                <SelectItem value="call_center">Focused on Call</SelectItem>
                <SelectItem value="cash_documentation">Cash & Documentation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
              Receptionist{" "}
              <span className="text-[#C9A24B] normal-case tracking-normal">({filteredReceptionists.length} loaded)</span>
            </Label>
            <Select value={empId || undefined} onValueChange={setEmpId} disabled={user.role === "receptionist" || !!editingLog}>
              <SelectTrigger data-testid="log-receptionist-select" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue placeholder="Select receptionist…" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                {filteredReceptionists.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500 italic">No active receptionists match</div>
                )}
                {filteredReceptionists.map((r) => (
                  <SelectItem key={r.employee_id} value={r.employee_id}>
                    {r.name} · {profilesOf(r).map((p) => PROFILE_LABEL[p]).join(" + ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">Date</Label>
            <Input
              data-testid="log-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={!!editingLog}
              className="bg-[#0F172A] border-[#334155] text-white disabled:opacity-50"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={resetFilters}
              data-testid="reset-filters-btn"
              className="h-11 w-full bg-[#1E293B] hover:bg-[#334155] text-slate-200 border border-[#334155]"
            >
              <RotateCcw size={14} className="mr-2" /> Reset Filters
            </Button>
          </div>
        </div>
        {currentRec && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Assigned profiles:</span>
            {currentProfiles.map((p) => (
              <Badge key={p} className="bg-[#C9A24B]/15 text-[#C9A24B] border border-[#C9A24B]/40 font-mono text-[10px] uppercase">
                {PROFILE_LABEL[p]}
              </Badge>
            ))}
            {callingRequired && callingTarget > 0 && (
              <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 font-mono text-[10px]">
                📞 Target {callingTarget}/day
              </Badge>
            )}
          </div>
        )}
      </Card>

      {currentRec && currentProfiles.includes("call_center") && callingTarget > 0 && (
        <Card className="bg-gradient-to-br from-[#0F172A] to-[#1a2235] border-[#C9A24B]/40 p-5" data-testid="calling-performance-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#C9A24B]/15 flex items-center justify-center text-[#C9A24B]">
                <Phone size={18} />
              </div>
              <div>
                <div className="overline">Calling Performance</div>
                <div className="font-heading text-white text-lg">Target vs Actual · Today</div>
              </div>
            </div>
            <div className="flex items-center gap-6 font-mono text-sm">
              <div className="text-right"><div className="text-slate-500 text-[10px] uppercase tracking-widest">Target</div><div className="text-white text-xl font-heading">{callingTarget}</div></div>
              <div className="text-right"><div className="text-slate-500 text-[10px] uppercase tracking-widest">Actual</div><div className="text-white text-xl font-heading">{callsTalked}</div></div>
              <div className="text-right"><div className="text-slate-500 text-[10px] uppercase tracking-widest">Diff</div><div className={`text-xl font-heading ${callsTalked - callingTarget >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{callsTalked - callingTarget >= 0 ? "+" : ""}{callsTalked - callingTarget}</div></div>
              <div className="text-right"><div className="text-slate-500 text-[10px] uppercase tracking-widest">Achievement</div><div className="text-xl font-heading" style={{ color: callingAchievement >= 100 ? "#059669" : callingAchievement >= 60 ? "#F59E0B" : "#DC2626" }}>{callingAchievement}%</div></div>
            </div>
          </div>
        </Card>
      )}

      {currentRec && (
        <>
          <Card className="bg-[#1E293B] border-[#334155] p-6">
            <div className="flex items-center gap-2 mb-5">
              <ClipboardCheck size={18} className="text-[#C9A24B]" />
              <h3 className="font-heading text-xl text-white">Core Metrics (shared)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedFields.map(renderField)}
            </div>
          </Card>

          {currentProfiles.map((pt) => {
            const fields = fieldsByProfile[pt] || [];
            if (!fields.length) return null;
            const ach = sectionAchievement(fields);
            return (
              <Card key={pt} className="bg-[#1E293B] border-[#334155] p-6" data-testid={`section-${pt}`}>
                <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck size={18} className="text-[#C9A24B]" />
                    <h3 className="font-heading text-xl text-white">{PROFILE_LABEL[pt]} · KPIs</h3>
                  </div>
                  {ach !== null && (
                    <Badge className="bg-slate-800 border border-slate-600 text-slate-200 font-mono text-xs" data-testid={`achievement-${pt}`}>
                      Section Achievement: {ach}%
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {fields.map(renderField)}
                </div>
              </Card>
            );
          })}

          <Card className="bg-[#1E293B] border-[#334155] p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">Feedback Notes</Label>
                <Textarea data-testid="log-feedback-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#0F172A] border-[#334155] text-white" />
              </div>
              <div>
                <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">Remarks</Label>
                <Textarea data-testid="log-remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="bg-[#0F172A] border-[#334155] text-white" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              {editingLog && (
                <Button onClick={cancelEdit} data-testid="log-cancel-btn" className="bg-slate-700 hover:bg-slate-600 text-white h-11 px-5">Cancel Edit</Button>
              )}
              <Button onClick={submit} data-testid="log-save-btn" className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11 px-6">
                <Save size={16} className="mr-2" /> {editingLog ? "Update Log" : "Save Daily Log"}
              </Button>
            </div>
          </Card>
        </>
      )}

      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <h3 className="font-heading text-xl text-white mb-4">Recent Entries</h3>
        <div className="divide-y divide-[#334155]">
          {recentLogs.length === 0 && <div className="text-sm text-slate-500 py-3">No entries yet.</div>}
          {recentLogs.map((l) => (
            <div key={l.log_id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`recent-log-${l.log_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-white font-mono">{l.date}</div>
                  {l.last_edited_at && <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/40 text-[10px] font-mono">edited</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Revenue ₹{(l.values?.total_revenue || 0).toLocaleString("en-IN")} · Feedback {l.values?.feedback_score || "-"}★ · Compliance {l.values?.compliance_score || 0}%
                  {l.values?.calls_talked !== undefined && ` · Calls ${l.values.calls_talked}`}
                </div>
                <div className="text-[10px] text-slate-600 mt-1 font-mono">
                  by {l.created_by_email || "—"}{l.last_edited_by && ` · last edit ${l.last_edited_by}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => beginEdit(l)} data-testid={`edit-log-${l.log_id}`} className="h-8 text-xs bg-[#1E293B] hover:bg-[#334155] text-[#C9A24B] border border-[#334155]">
                  <Edit size={12} className="mr-1" /> Edit
                </Button>
                <Button size="sm" onClick={() => deleteLog(l.log_id)} data-testid={`delete-log-${l.log_id}`} className="h-8 text-xs bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/40">
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default DailyLog;
